import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { isValidPreviewToken } from '../utils/previewToken';
import { mapPreviewProperty, PreviewProperty } from '../utils/previewProperty';

/**
 * Legge un immobile dal link privato.
 *
 * Passa dalla funzione get_property_preview e non dalla tabella: la RLS
 * pubblica su properties mostra solo gli immobili pubblicati, quindi una
 * select normale non vedrebbe mai una bozza.
 *
 * 'unavailable' copre tutti i casi in cui il link non porta a niente: token
 * malformato, revocato, mai esistito. Sono indistinguibili di proposito, così
 * la pagina non fa da oracolo a chi prova token a caso.
 */
export type PreviewState = 'loading' | 'ok' | 'unavailable' | 'error';

export const usePropertyPreview = (token: string) => {
  const [property, setProperty] = useState<PreviewProperty | null>(null);
  const [state, setState] = useState<PreviewState>('loading');

  useEffect(() => {
    let cancelled = false;

    const fetchPreview = async () => {
      setState('loading');

      // Token malformato: nessuna chiamata, la risposta è già decisa.
      if (!isValidPreviewToken(token)) {
        if (!cancelled) {
          setProperty(null);
          setState('unavailable');
        }
        return;
      }

      const { data, error } = await supabase.rpc('get_property_preview', { p_token: token });

      if (cancelled) return;

      if (error) {
        console.error('Errore nel caricamento anteprima:', error);
        setProperty(null);
        setState('error');
        return;
      }

      const mapped = mapPreviewProperty(data);
      setProperty(mapped);
      setState(mapped ? 'ok' : 'unavailable');
    };

    fetchPreview();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return { property, state };
};
